import { useEffect, useMemo } from 'react'
import { fromPairs } from 'lodash'

import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import useToggleSdkContext from 'utils/useToogleSdk'
import { PeriodContext } from 'app/KinerjaShared/hooks/usePeriodContext'
import { useAPIGetCurrentPeriodV2 } from 'api/kinerja/useAPIKinerja'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { formatDate, isTodayBetweenDates } from 'utils/datetime'
import { getSession } from 'utils/auth'
import { IS_DEVELOPMENT } from 'app/KinerjaShared/constants/env'

import type { SKPPeriodV2 } from 'types/kinerja'

interface Props {
  children: React.ReactNode
  onPeriodChange?: (period: SKPPeriodV2) => void
}

const defaultYear = Math.max(new Date().getFullYear(), 2024)

const defaultPeriod: SKPPeriodV2 = {
  id: '',
  isApproved: false,
  startDate: formatDate(new Date(defaultYear, 0, 1), 'yyyy-MM-dd'),
  endDate: formatDate(new Date(defaultYear, 5, 1), 'yyyy-MM-dd'),
  period: 1,
  periodeKinerja: [],
  accessible: true, // Not blocked
}

type PeriodStorage = Record<string, string>
export const KINERJA_PERIOD_STORAGE_KEY = 'kinerja-period-data'

/**
 * Provides the current period information.
 */
export default function PeriodProvider({ children, onPeriodChange }: Props) {
  const session = getSession()
  const toggles = useToggleSdkContext()
  const isToggleEnabled = toggles?.['kinerja-use-multiple-period'] ?? false
  const isMultiplePeriodEnabled = isToggleEnabled

  const [allValues, setValues] = useLocalStorage(KINERJA_PERIOD_STORAGE_KEY, {})
  const values = allValues as PeriodStorage

  // Last visited period
  const periodId = useMemo(
    () => values[session?.user?.id] ?? '',
    [values, session?.user?.id]
  )

  const setValue = (periodId: string) => {
    const lastValues = fromPairs(Object.entries(values).slice(-10))

    setValues({
      ...lastValues,
      [session?.user?.id]: periodId,
    })
  }

  const { data, isFetched, isLoading } = useAPIGetCurrentPeriodV2({
    useErrorBoundary: true,
    staleTime: 60 * 60 * 1000, // 1 hour
    onSuccess(data) {
      // If there is no last visited period (i.e. periodId),
      // set the meta.latestActiveId as the last visited period.
      // The `meta.latestActiveId` contains the latest visitable period of current user
      if (!periodId && data.meta?.latestActiveId) {
        const latestActivePeriodId = data.meta?.latestActiveId

        setValue(latestActivePeriodId)
      }
    },
  })

  const period = useMemo(() => {
    return data?.data?.find((item) => item.id === periodId) ?? defaultPeriod
  }, [data, periodId])

  const isCurrentPeriod = useMemo(() => {
    return isTodayBetweenDates(
      new Date(period?.startDate),
      new Date(period?.endDate)
    )
  }, [period])

  useEffect(() => {
    onPeriodChange?.(period)
  }, [onPeriodChange, period])

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex flex-col gap-4 justify-center items-center h-screen"
      >
        <Spinner data-testid="loading-spinner-period" />
        {IS_DEVELOPMENT && <Text>Loading period data...</Text>}
      </div>
    )
  }

  return (
    <PeriodContext.Provider
      value={{
        isEnabled: isMultiplePeriodEnabled,
        isHavePeriod: Boolean(periodId),
        periodYear: new Date(period.startDate).getFullYear(),
        periodNo: period.period,
        period,
        setPeriod: ({ id }) => setValue(id),
        isFetched,
        isCurrentPeriod,

        // Data period
        periods: data?.data,

        // Helpers
        getPeriod: ({ periodYear, periodNo }) => {
          return data?.data?.find(
            (p) =>
              new Date(p.startDate).getFullYear() === periodYear &&
              p.period === periodNo
          )
        },
      }}
    >
      {children}
    </PeriodContext.Provider>
  )
}
